import { getFunctions, httpsCallable } from 'firebase/functions';
import axios from 'axios';

/**
 * Ödeme servis katmanı
 * Firebase Functions ile iletişimi yönetir
 */

// Local test modu - production'da false yapın
const USE_LOCAL_PROXY = false; // Firebase Functions kullan

class PaymentService {
  constructor() {
    this.functions = getFunctions(undefined, 'europe-west1');
  }

  /**
   * Kart numarası validasyonu (Luhn algoritması)
   */
  validateCardNumber(cardNumber) {
    const cleaned = cardNumber.replace(/\s/g, '');
    if (!/^\d{16}$/.test(cleaned)) {
      return false;
    }

    let sum = 0;
    let isEven = false;
    
    for (let i = cleaned.length - 1; i >= 0; i--) {
      let digit = parseInt(cleaned[i], 10);
      
      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }
      
      sum += digit;
      isEven = !isEven;
    }
    
    return sum % 10 === 0;
  }

  /**
   * Kart tipini belirle
   */
  getCardType(cardNumber) {
    const cleaned = cardNumber.replace(/\s/g, '');
    
    // KuveytTürk beklenen değerler: Visa, MasterCard, Troy
    if (/^4/.test(cleaned)) return 'Visa';
    if (/^5[1-5]/.test(cleaned)) return 'MasterCard';
    if (/^9792/.test(cleaned)) return 'Troy';
    
    return 'Visa';
  }

  /**
   * Expiry date validasyonu
   */
  validateExpiryDate(expiryDate) {
    const [month, year] = expiryDate.split('/');
    const monthNum = parseInt(month, 10);
    const yearNum = parseInt('20' + year, 10);
    
    if (monthNum < 1 || monthNum > 12) {
      return false;
    }
    
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    
    if (yearNum < currentYear) {
      return false;
    }
    
    if (yearNum === currentYear && monthNum < currentMonth) {
      return false;
    }
    
    return true;
  }

  /**
   * Kart bilgilerini validate et
   */
  validateCardDetails(cardDetails) {
    const errors = {};
    
    // Kart numarası kontrolü
    if (!cardDetails.cardNumber) {
      errors.cardNumber = 'Kart numarası gereklidir';
    } else if (!this.validateCardNumber(cardDetails.cardNumber)) {
      errors.cardNumber = 'Geçersiz kart numarası';
    }
    
    // Kart sahibi adı kontrolü
    if (!cardDetails.cardName) {
      errors.cardName = 'Kart üzerindeki isim gereklidir';
    } else if (cardDetails.cardName.length < 3) {
      errors.cardName = 'İsim en az 3 karakter olmalıdır';
    }
    
    // Expiry date kontrolü
    if (!cardDetails.expiryDate) {
      errors.expiryDate = 'Son kullanma tarihi gereklidir';
    } else if (!this.validateExpiryDate(cardDetails.expiryDate)) {
      errors.expiryDate = 'Geçersiz veya süresi geçmiş tarih';
    }
    
    // CVV kontrolü
    if (!cardDetails.cvv) {
      errors.cvv = 'CVV kodu gereklidir';
    } else if (!/^\d{3,4}$/.test(cardDetails.cvv)) {
      errors.cvv = 'CVV 3 veya 4 haneli olmalıdır';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  /**
   * Ödeme işlemini başlat (3D Secure)
   */
  async initiatePayment(paymentData) {
    try {
      // Kart bilgilerini validate et
      const validation = this.validateCardDetails(paymentData.cardDetails);
      if (!validation.isValid) {
        throw new Error('Kart bilgileri geçersiz: ' + Object.values(validation.errors).join(', '));
      }

      // Kart bilgilerini hazırla
      const [expiryMonth, expiryYear] = paymentData.cardDetails.expiryDate.split('/');
      
      const requestData = {
        amount: paymentData.amount,
        packageInfo: paymentData.packageInfo,
        cardDetails: {
          cardNumber: paymentData.cardDetails.cardNumber.replace(/\s/g, ''),
          cardName: paymentData.cardDetails.cardName.toUpperCase(),
          expiryMonth: expiryMonth.padStart(2, '0'), // 2 haneli olmalı
          expiryYear: expiryYear.padStart(2, '0'), // 2 haneli olmalı
          cvv: paymentData.cardDetails.cvv,
          cardType: this.getCardType(paymentData.cardDetails.cardNumber)
        },
        customerInfo: paymentData.customerInfo || {}
      };

      // Local proxy kullan (TEST)
      if (USE_LOCAL_PROXY) {
        console.log('🔧 Local proxy kullanılıyor...');
        try {
          const response = await axios.post('http://localhost:5000/api/initiatePayment', requestData);
          return response.data;
        } catch (error) {
          console.error('Local proxy hatası:', error);
          throw new Error('Local proxy çalışmıyor. Terminal\'de "npm run proxy" komutunu çalıştırın.');
        }
      }
      
      // Firebase Function'ı çağır (PRODUCTION)
      const initiatePaymentFunc = httpsCallable(this.functions, 'initiatePayment');
      const result = await initiatePaymentFunc(requestData);

      return result.data;
    } catch (error) {
      console.error('Payment initiation error:', error);
      throw error;
    }
  }

  /**
   * 3D Secure doğrulama sonrası ödemeyi onayla
   */
  async confirmPayment(confirmationData) {
    try {
      const confirmPaymentFunc = httpsCallable(this.functions, 'confirmPayment');
      const result = await confirmPaymentFunc(confirmationData);
      
      return result.data;
    } catch (error) {
      console.error('Payment confirmation error:', error);
      throw error;
    }
  }

  /**
   * 3D Secure HTML formunu render et
   */
  render3DSecureForm(htmlContent) {
    if (!htmlContent) {
      throw new Error('Bankadan dönen 3D Secure içeriği bulunamadı');
    }

    const doc = window.document;
    doc.open('text/html', 'replace');
    doc.write(htmlContent);
    doc.close();

    return true;
  }

  /**
   * Test kartları için bilgi
   */
  getTestCards() {
    return [
      {
        type: 'Visa',
        number: '4111 1111 1111 1111',
        cvv: '123',
        expiry: '12/25',
        description: 'Test kartı - Başarılı ödeme'
      },
      {
        type: 'MasterCard',
        number: '5555 5555 5555 4444',
        cvv: '123',
        expiry: '12/25',
        description: 'Test kartı - Başarılı ödeme'
      },
      {
        type: 'Troy',
        number: '9792 1234 5678 9012',
        cvv: '123',
        expiry: '12/25',
        description: 'Test kartı - Troy'
      }
    ];
  }

  /**
   * Ödeme durumunu kontrol et
   */
  async checkPaymentStatus(merchantOrderId) {
    try {
      // Firestore'dan ödeme durumunu kontrol et
      // Bu fonksiyon ileride eklenebilir
      return {
        status: 'pending',
        merchantOrderId
      };
    } catch (error) {
      console.error('Payment status check error:', error);
      throw error;
    }
  }
}

export default new PaymentService();
